import type { AnimalItem, ShareItem } from "../mocks/animals";
import type { CustomerItem } from "../mocks/customers";
import type { OdemeTipi, PaymentRecord } from "../mocks/payments";
import { createClientNumericId } from "./id";
import {
  getShareTotal,
  isShareOwnedByCustomer,
  recalcAnimalTotals,
  recalcCustomersFromAnimals,
} from "./cascadeOps";

export type ExtendedShare = ShareItem & {
  slotNos?: number[];
  satisTarihi?: string;
  satisNotu?: string;
  satisFisNo?: string;
};

export type ExtendedPaymentRecord = PaymentRecord & {
  fisNo?: string;
  belgeNo?: string;
};

export interface ShareWithAnimal {
  animal: AnimalItem;
  share: ShareItem;
}

export interface PaymentFormPayload {
  tarih: string;
  tutar: number;
  tip: OdemeTipi;
  aciklama: string;
  havaleNo?: string;
  fisNo?: string;
}

export interface CreateGroupedSaleInput {
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
  customer: CustomerItem;
  animalId: number;
  slotNos: number[];
  birimSatisTutar: number;
  kesimUcreti: number;
  parcalamaUcreti: number;
  tasimaUcreti: number;
  tasimaVar?: boolean;
  indirim: number;
  tarih: string;
  kisaNot: string;
  pesinatTutar: number;
  pesinatTip: OdemeTipi;
  pesinatHavaleNo?: string;
  pesinatDekontNo?: string;
  pesinatAciklama?: string;
  fisNo?: string;
}

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\D/g, "");
}

function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

export function asExtendedShare(share: ShareItem) {
  return share as ExtendedShare;
}

export function getShareSlotNos(share: ShareItem): number[] {
  const meta = asExtendedShare(share);

  if (Array.isArray(meta.slotNos) && meta.slotNos.length > 0) {
    return [...meta.slotNos].sort((a, b) => a - b);
  }

  const lastPart = share.hisseNo.split("-").pop() ?? "";

  return lastPart
    .split("+")
    .map((item) => parseInt(item.trim(), 10))
    .filter((value) => !Number.isNaN(value))
    .sort((a, b) => a - b);
}

export function buildGroupedHisseNo(siraNo: number, slotNos: number[]) {
  return `H-${String(siraNo).padStart(3, "0")}-${slotNos.join("+")}`;
}

export function getShareSortKey(share: ShareItem) {
  const slotNos = getShareSlotNos(share);
  return slotNos.length > 0 ? slotNos[0] : 0;
}

export function findCustomerShares(
  customer: CustomerItem,
  animals: AnimalItem[]
): ShareWithAnimal[] {
  const result: ShareWithAnimal[] = [];

  for (const animal of animals) {
    for (const share of animal.hisseler ?? []) {
      if (isShareOwnedByCustomer(share, customer)) {
        result.push({ animal, share });
      }
    }
  }

  return result.sort(
    (a, b) =>
      a.animal.id - b.animal.id ||
      getShareSortKey(a.share) - getShareSortKey(b.share)
  );
}

export function findCustomerByShare(
  customers: CustomerItem[],
  share: ShareItem
): CustomerItem | null {
  if (typeof share.musteriId === "number") {
    const byId = customers.find((customer) => customer.id === share.musteriId);
    if (byId) return byId;
  }

  const sharePhone = normalizePhone(share.telefon);
  const shareName = normalizeText(share.musteriAdi);

  return (
    customers.find((customer) => {
      if (
        sharePhone &&
        normalizePhone(customer.telefon) &&
        normalizePhone(customer.telefon) === sharePhone
      ) {
        return true;
      }

      return normalizeText(customer.adSoyad) === shareName;
    }) ?? null
  );
}

export function createGroupedSale(input: CreateGroupedSaleInput) {
  const {
    animals,
    customers,
    payments,
    customer,
    animalId,
    slotNos,
    birimSatisTutar,
    kesimUcreti,
    parcalamaUcreti,
    tasimaUcreti,
    tasimaVar,
    indirim,
    tarih,
    kisaNot,
    pesinatTutar,
    pesinatTip,
    pesinatHavaleNo,
    pesinatDekontNo,
    pesinatAciklama,
    fisNo,
  } = input;

  const animal = animals.find((item) => item.id === animalId);
  if (!animal) {
    return {
      animals,
      customers,
      payments,
      createdShareId: null as number | null,
    };
  }

  const uniqueSlotNos = Array.from(new Set(slotNos))
    .filter((value) => value > 0)
    .sort((a, b) => a - b);

  if (uniqueSlotNos.length === 0) {
    return {
      animals,
      customers,
      payments,
      createdShareId: null as number | null,
    };
  }

  const nextShareId = createClientNumericId();

  const hisseSayisi = uniqueSlotNos.length;
  const toplamSatisTutari = Math.max(0, birimSatisTutar);
  const toplamKesim = Math.max(0, kesimUcreti);
  const toplamParcalama = Math.max(0, parcalamaUcreti);
  const toplamTasima = Math.max(0, tasimaUcreti);
  const toplamIndirim = Math.max(0, indirim);

  const brutToplam =
    toplamSatisTutari +
    toplamKesim +
    toplamParcalama +
    toplamTasima -
    toplamIndirim;

  const safeTotal = Math.max(0, brutToplam);
  const safePesinat = Math.max(0, pesinatTutar);
  const safeDekont = pesinatHavaleNo?.trim() || undefined;
  const safePesinatAciklama = pesinatAciklama?.trim() || undefined;

  const newShare = {
    id: nextShareId,
    hisseNo: buildGroupedHisseNo(animal.siraNo, uniqueSlotNos),
    musteriId: customer.id,
    musteriAdi: customer.adSoyad,
    telefon: customer.telefon,
    hisseSayisi,
    tutar: toplamSatisTutari,
    kesimUcreti: toplamKesim > 0 ? toplamKesim : undefined,
    parcalamaUcreti: toplamParcalama > 0 ? toplamParcalama : undefined,
    tasimaUcreti: toplamTasima > 0 ? toplamTasima : undefined,
    tasimaVar: Boolean(tasimaVar || toplamTasima > 0),
    indirim: toplamIndirim > 0 ? toplamIndirim : undefined,
    odenen: safePesinat,
    kalan: safeTotal - safePesinat,
    durum: safePesinat === 0 ? "Bekliyor" : safePesinat >= safeTotal ? "Tam" : "Kısmi",
    slotNos: uniqueSlotNos,
    satisTarihi: tarih,
    satisNotu: kisaNot.trim() || undefined,
    satisFisNo: fisNo?.trim() || undefined,
    pesinatAciklama: pesinatAciklama?.trim() || undefined,
    pesinatDekontNo:
      pesinatDekontNo?.trim() || pesinatHavaleNo?.trim() || undefined,
  } as ShareItem;

  const nextAnimals = animals.map((item) => {
    if (item.id !== animalId) return item;

    const nextAnimal = {
      ...item,
      hisseler: [...(item.hisseler ?? []), newShare].sort(
        (a, b) => getShareSortKey(a) - getShareSortKey(b)
      ),
    };

    return recalcAnimalTotals(nextAnimal);
  });

  let nextPayments = [...payments];

  if (safePesinat > 0) {
    const paymentRecord = {
      id: createClientNumericId(),
      tarih,
      musteriAdi: customer.adSoyad,
      telefon: customer.telefon,
      musteriId: customer.id,
      hayvanKupeNo: animal.kupeNo,
      animalId: animal.id,
      hisseNo: newShare.hisseNo,
      shareId: nextShareId,
      tutar: safePesinat,
      tip: pesinatTip,
      aciklama: safePesinatAciklama ?? "",
      havaleNo:
        pesinatDekontNo?.trim() ||
        pesinatHavaleNo?.trim() ||
        undefined,
      olusturan: "Admin",
      fisNo: fisNo?.trim() || undefined,
    } as PaymentRecord;

    nextPayments = [paymentRecord, ...nextPayments];
  }

  const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: nextPayments,
    createdShareId: nextShareId,
  };
}

export function applyPaymentToSelectedShares(params: {
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
  customer: CustomerItem;
  selectedShares: ShareWithAnimal[];
  data: PaymentFormPayload;
}) {
  const { animals, customers, payments, customer, selectedShares, data } = params;

  let remaining = Math.max(0, data.tutar);
  const allocations: Array<{
    animalId: number;
    shareId: number;
    animalKupeNo: string;
    animalSiparisNo: string;
    hisseNo: string;
    amount: number;
  }> = [];

  const orderedShares = [...selectedShares].sort(
    (a, b) =>
      a.animal.id - b.animal.id ||
      getShareSortKey(a.share) - getShareSortKey(b.share)
  );

  for (const item of orderedShares) {
    if (remaining <= 0) break;

    const available = Math.max(0, getShareTotal(item.share) - item.share.odenen);
    if (available <= 0) continue;

    const amount = Math.min(available, remaining);
    remaining -= amount;

    allocations.push({
      animalId: item.animal.id,
      shareId: item.share.id,
      animalKupeNo: item.animal.kupeNo,
      animalSiparisNo: String(item.animal.siraNo ?? ""),
      hisseNo: item.share.hisseNo,
      amount,
    });
  }

  // Tartım/kapora senaryosu: müşteri kalan borçtan fazla ödeme yapabilir.
  // Fazla tutar ilk seçili hisseye alacak/eksi bakiye olarak eklenir.
  if (remaining > 0 && orderedShares.length > 0) {
    const first = orderedShares[0];
    const existing = allocations.find((item) => item.shareId === first.share.id);

    if (existing) {
      existing.amount += remaining;
    } else {
      allocations.push({
        animalId: first.animal.id,
        shareId: first.share.id,
        animalKupeNo: first.animal.kupeNo,
        animalSiparisNo: String(first.animal.siraNo ?? ""),
        hisseNo: first.share.hisseNo,
        amount: remaining,
      });
    }

    remaining = 0;
  }

  if (allocations.length === 0) {
    return {
      animals,
      customers,
      payments,
      completedAnimalIds: [] as number[],
    };
  }

  const nextAnimals = animals.map((animal) => {
    const animalAllocations = allocations.filter(
      (allocation) => allocation.animalId === animal.id
    );

    if (animalAllocations.length === 0) return animal;

    const nextAnimal = {
      ...animal,
      hisseler: animal.hisseler.map((share) => {
        const match = animalAllocations.find(
          (allocation) => allocation.shareId === share.id
        );

        if (!match) return share;

        return {
          ...share,
          odenen: share.odenen + match.amount,
        };
      }),
    };

    return recalcAnimalTotals(nextAnimal);
  });

  const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

  const newPayments: PaymentRecord[] = allocations.map((allocation) => {
    return {
      id: createClientNumericId(),
      tarih: data.tarih,
      musteriAdi: customer.adSoyad,
      telefon: customer.telefon,
      musteriId: customer.id,
      hayvanKupeNo: allocation.animalKupeNo,
      hayvanSiparisNo: allocation.animalSiparisNo,
      animalId: allocation.animalId,
      hisseNo: allocation.hisseNo,
      shareId: allocation.shareId,
      tutar: allocation.amount,
      tip: data.tip,
      aciklama: data.aciklama?.trim() || "",
      havaleNo: data.havaleNo?.trim() || undefined,
      olusturan: "Admin",
      fisNo: data.fisNo?.trim() || (data.tip === "Havale" ? data.havaleNo?.trim() : undefined) || undefined,
    };
  });

  const completedAnimalIds = Array.from(
    new Set(allocations.map((allocation) => allocation.animalId))
  ).filter((animalId) => {
    const beforeAnimal = animals.find((item) => item.id === animalId);
    const afterAnimal = nextAnimals.find((item) => item.id === animalId);

    if (!beforeAnimal || !afterAnimal) return false;

    const beforeIncomplete = beforeAnimal.hisseler.some((share) => share.kalan > 0);
    const afterComplete =
      afterAnimal.hisseler.length > 0 &&
      afterAnimal.hisseler.every((share) => share.kalan === 0);

    return beforeIncomplete && afterComplete;
  });

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: [...newPayments, ...payments],
    completedAnimalIds,
  };
}
